import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  subscribeToClients,
  subscribeToAllObjects,
  isObjectIncomplete,
} from '../utils/storage';
import CreateObjectModal from './CreateObjectModal';
import LoadingScreen from './LoadingScreen';
import { SearchBar, EmptyState } from './templates';
import './ObjectsPage.css';
import './Modal.css';

function fmtDate(dateStr) {
  if (!dateStr) return '—';
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

export default function ObjectsPage() {
  const navigate = useNavigate();

  const [clients,    setClients]    = useState([]);
  const [allObjects, setAllObjects] = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [pageReady,  setPageReady]  = useState(false);

  // Modal phase: null | 'selectSite' | 'createObject'
  const [modalPhase,      setModalPhase]      = useState(null);
  const [selectedClient,  setSelectedClient]  = useState(null);

  // Search
  const [search, setSearch] = useState('');

  useEffect(() => {
    let clientsReady = false;
    let objectsReady = false;

    const unsub1 = subscribeToClients((list) => {
      setClients(list.sort((a, b) => a.name.localeCompare(b.name)));
      clientsReady = true;
      if (clientsReady && objectsReady) setLoading(false);
    });

    const unsub2 = subscribeToAllObjects((list) => {
      setAllObjects(list);
      objectsReady = true;
      if (clientsReady && objectsReady) setLoading(false);
    });

    return () => { unsub1(); unsub2(); };
  }, []);

  const clientMap = Object.fromEntries(clients.map((c) => [c.id, c]));

  const enrichedObjects = allObjects
    .map((obj) => ({ ...obj, clientName: clientMap[obj.clientId]?.name || '—' }))
    .sort((a, b) => {
      const da = a.generalInfo?.startDate || '';
      const db = b.generalInfo?.startDate || '';
      if (!da && !db) return 0;
      if (!da) return 1;
      if (!db) return -1;
      return da.localeCompare(db);
    });

  const q = search.trim().toLowerCase();
  const filtered = q
    ? enrichedObjects.filter(
        (o) =>
          (o.objectTag || '').toLowerCase().includes(q) ||
          (o.type || '').toLowerCase().includes(q) ||
          o.clientName.toLowerCase().includes(q)
      )
    : enrichedObjects;

  function openSiteSelector() {
    setSelectedClient(null);
    setModalPhase('selectSite');
  }

  function handleSiteSelected(client) {
    setSelectedClient(client);
    setModalPhase('createObject');
  }

  function handleObjectCreated() {
    setModalPhase(null);
    setSelectedClient(null);
  }

  function closeModal() {
    setModalPhase(null);
    setSelectedClient(null);
  }

  if (!pageReady) {
    return (
      <LoadingScreen
        isLoading={loading}
        onDone={() => setPageReady(true)}
      />
    );
  }

  return (
    <div className="page-container">

      {/* Toolbar */}
      <div className="toolbar">
        <SearchBar
          value={search}
          onChange={setSearch}
          placeholder="Search ID, type, site…"
        />
        <button className="btn btn-primary" onClick={openSiteSelector}>
          + Add Object
        </button>
      </div>

      {/* Empty state */}
      {filtered.length === 0 && (
        <EmptyState icon="📦">
          {q ? (
            <p>No objects match your search.</p>
          ) : (
            <>
              <h2>No objects yet</h2>
              <p>Objects are created inside a test site. Click below to get started.</p>
              <button className="btn btn-primary" onClick={openSiteSelector}>+ Add Object</button>
            </>
          )}
        </EmptyState>
      )}

      {/* Objects table */}
      {filtered.length > 0 && (
        <div className="table-wrapper">
          <table className="ds-table ds-table-fixed objects-table">
            <colgroup>
              <col className="col-date" />
              <col className="col-id" />
              <col className="col-type" />
              <col className="col-site" />
              <col className="col-edit" />
            </colgroup>
            <thead>
              <tr>
                <th>Start Date</th>
                <th>Object ID ({filtered.length})</th>
                <th>Type</th>
                <th>Test Site</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((obj) => {
                const isIncomplete = isObjectIncomplete(obj);
                return (
                  <tr
                    key={obj.id}
                    className="objects-row"
                    onClick={() => navigate(`/client/${obj.clientId}/object/${obj.id}`)}
                  >
                    <td className="obj-start-date">
                      {fmtDate(obj.generalInfo?.startDate || '')}
                    </td>
                    <td>
                      <div className="obj-id-cell">
                        <span className="obj-tag">{obj.objectTag || '—'}</span>
                        {isIncomplete && (
                          <span className="obj-incomplete-chip">INCOMPLETE</span>
                        )}
                      </div>
                    </td>
                    <td className="obj-type">{obj.type || '—'}</td>
                    <td className="obj-site">{obj.clientName}</td>
                    <td className="obj-chevron-cell">›</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Test Site Selector Modal ── */}
      {modalPhase === 'selectSite' && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Select Test Site</h2>
              <button className="modal-close" onClick={closeModal}>✕</button>
            </div>
            <p className="confirm-text">Choose the test site this object belongs to.</p>

            {clients.length > 0 ? (
              <div className="site-select-list">
                {clients.map((c) => (
                  <div
                    key={c.id}
                    className="site-select-item"
                    onClick={() => handleSiteSelected(c)}
                  >
                    <div>
                      <div className="site-select-item-name">{c.name}</div>
                      {c.clientName && (
                        <div className="site-select-item-meta">{c.clientName}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="site-select-empty">
                <div className="site-select-empty-icon">🏗</div>
                <p>No test sites available yet.</p>
              </div>
            )}

            <div className="modal-actions">
              <button className="btn btn-secondary" onClick={closeModal}>Cancel</button>
              {clients.length === 0 && (
                <button
                  className="btn btn-primary"
                  onClick={() => { closeModal(); navigate('/test-sites'); }}
                >
                  Create Test Site
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Create Object Modal ── */}
      {modalPhase === 'createObject' && selectedClient && (
        <CreateObjectModal
          clientId={selectedClient.id}
          onClose={closeModal}
          onCreated={handleObjectCreated}
        />
      )}

    </div>
  );
}
